// Generated file : do not modify
export enum RessourceSources
{
  NONE = 0,
  SERVER,
  CLIENT
};

export enum RessourceTypes
{
  NONE = 0,
  BOUTON,
  TUILE,
  CONTROL,
  MODULE,
  FICHE,
  SECTION,
  HEADER,
  CUSTOM,
  DIALOG,
  CHAT_ITEM,
  DOCUMENT,
  WIZARD,
  GRAPH_QUESTION
};

import { NotifDefTypes } from '@/generated/classes/NotifDef';
import { storeSession } from '@/generated/stores';
import { type AxiosResponse } from 'axios';

export interface RessourceUpdated {
  (): void;
}

// Ressources (libellés, aides...)
export default class Ressource {
  // Identifiant unique
  public id: number;
  // Type de ressource
  public source: RessourceSources;
  // Contexte
  public type: RessourceTypes;
  // Label mis à jour
  public hasUpdate: boolean;
  // Label spécifique pour mobile
  public hasMobile: boolean;
  // Nom de la ressource
  public name: string;
  // Libellé
  public label: string;
  // Libellé mobile
  public labelMobile: string;
  // placeholder associé à la ressource
  public placeholder: string;
  // Aide associé à la ressource
  public help: string;
  // Aide associé à la ressource
  public helpHtmlId: number;
  // Type de notification
  public notifDefType: NotifDefTypes;
  // Donnée pouvant être  modifiée
  public canUpdate: boolean;
  // Donnée pouvant être supprimée
  public canDelete: boolean;

  public updated: RessourceUpdated;

  constructor() {
    this.id = 0;
    this.source = RessourceSources.NONE;
    this.type = RessourceTypes.NONE;
    this.hasUpdate = false;
    this.hasMobile = false;
    this.name = '';
    this.label = '';
    this.labelMobile = '';
    this.placeholder = '';
    this.help = '';
    this.helpHtmlId = 0;
    this.notifDefType = NotifDefTypes.NONE;
    this.canUpdate = true;
    this.canDelete = true;
  }

  public static fromJson (obj: Ressource): Ressource {
    const newRessource: Ressource = new Ressource();
    newRessource.id = obj.id;
    newRessource.source = obj.source;
    newRessource.type = obj.type;
    newRessource.hasUpdate = obj.hasUpdate;
    newRessource.hasMobile = obj.hasMobile;
    newRessource.name = obj.name;
    newRessource.label = obj.label;
    newRessource.labelMobile = obj.labelMobile;
    newRessource.placeholder = obj.placeholder;
    newRessource.help = obj.help;
    newRessource.helpHtmlId = obj.helpHtmlId;
    newRessource.notifDefType = obj.notifDefType;
    newRessource.canUpdate = obj.canUpdate;
    newRessource.canDelete = obj.canDelete;
    return newRessource;
  }

  private toJson (): string {
    const json = {
      source: this.source,
      type: this.type,
      hasUpdate: this.hasUpdate,
      hasMobile: this.hasMobile,
      name: this.name,
      label: this.label,
      labelMobile: this.labelMobile,
      placeholder: this.placeholder,
      help: this.help,
      helpHtmlId: this.helpHtmlId,
      notifDefType: this.notifDefType,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
    }
    return JSON.stringify(json);
  }

  public fromRessource (obj: Ressource): void {
    this.id = obj.id;
    this.source = obj.source;
    this.type = obj.type;
    this.hasUpdate = obj.hasUpdate;
    this.hasMobile = obj.hasMobile;
    this.name = obj.name;
    this.label = obj.label;
    this.labelMobile = obj.labelMobile;
    this.placeholder = obj.placeholder;
    this.help = obj.help;
    this.helpHtmlId = obj.helpHtmlId;
    this.notifDefType = obj.notifDefType;
    this.canUpdate = obj.canUpdate;
    this.canDelete = obj.canDelete;
  }

  public compare (value: Ressource): boolean {
    return (this.toJson() == value.toJson());
  }

  public clone (): Ressource {
    return Ressource.fromJson(this);
  }

  public create (): Promise<Ressource> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.post('api/ressources/', this, storeSession.httpAuthor)
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              this.fromRessource(res.data);
              resolve(this);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public update (): Promise<Ressource> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.put('api/ressources/', this, storeSession.httpAuthor)
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              this.fromRessource(res.data);
              resolve(this);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static listAll (isMobile: boolean): Promise<Ressource[]> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.get('api/ressources/listAll/' + isMobile, storeSession.httpAuthor)
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              const cData: Ressource[] = [];
              res.data.forEach(( a: Ressource ) => {
                cData.push(a);
              })
              resolve(cData);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static listByType (ressource: Ressource): Promise<Ressource[]> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.post('api/ressources/listByType', ressource, storeSession.httpAuthor)
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              const cData: Ressource[] = [];
              res.data.forEach(( a: Ressource ) => {
                cData.push(a);
              })
              resolve(cData);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static get (id: number): Promise<Ressource> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.get('api/Ressources/' + id.toString(), storeSession.httpAuthor)
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              resolve(Ressource.fromJson(res.data));
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  // eslint-disable-next-line
  public static getDate(ressources: Ressource[]) {
  }
}
